import { Box } from '@mui/material';
import { AutocompleteItem, ChipsInput } from '@prismamedia/one-components';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import type { TabMultiselectChipVariant } from './styles';
import { getTabMultiselectFieldSx, tabMultiselectWrapperSx } from './styles';

interface TabMultiselectItem {
  id: string;
  title: string;
}

interface TabMultiselectProps {
  chipVariant: TabMultiselectChipVariant;
  fetchList: (search: string) => Promise<AutocompleteItem[] | undefined>;
  isSearchMandatory?: boolean;
  items: TabMultiselectItem[];
  label: string;
  maxQuantity: number;
  onChange: (items: TabMultiselectItem[]) => void;
  searchPlaceholder?: string;
}

const toAutocompleteItems = (items: TabMultiselectItem[]): AutocompleteItem[] =>
  items.map(({ id, title }) => ({
    id,
    label: title,
  }));

const fromAutocompleteItems = (
  value: AutocompleteItem[],
): TabMultiselectItem[] =>
  value.reduce<TabMultiselectItem[]>(
    (acc, { id, label }) =>
      id && typeof label === 'string' ? [...acc, { id, title: label }] : acc,
    [],
  );

export const TabMultiselect: FC<TabMultiselectProps> = ({
  chipVariant,
  fetchList,
  isSearchMandatory = false,
  items,
  label,
  maxQuantity,
  onChange,
  searchPlaceholder,
}) => {
  const fieldSx = useMemo(() => getTabMultiselectFieldSx(chipVariant), [
    chipVariant,
  ]);

  const isMaxReached = items.length >= maxQuantity;

  const selectedIds = useMemo(() => new Set(items.map(({ id }) => id)), [
    items,
  ]);

  const autocompleteValue = useMemo(() => toAutocompleteItems(items), [items]);

  const handleChipsChange = useCallback(
    (value: AutocompleteItem[]) => {
      onChange(fromAutocompleteItems(value));
    },
    [onChange],
  );

  const getFilteredList = useCallback(
    async (search: string) => {
      const list = await fetchList(search);
      return list?.filter(({ id }) => id && !selectedIds.has(id));
    },
    [fetchList, selectedIds],
  );

  return (
    <Box sx={tabMultiselectWrapperSx}>
      <ChipsInput
        autocomplete={{
          fetchList: getFilteredList,
          mandatoryInput: isSearchMandatory,
        }}
        disabled={isMaxReached}
        label={`${label} (${items.length}/${maxQuantity})`}
        maxLength={maxQuantity}
        onChange={handleChipsChange}
        TextFieldProps={{
          InputLabelProps: { shrink: true },
          placeholder: searchPlaceholder,
          sx: fieldSx,
        }}
        value={autocompleteValue}
        variant="outlined"
      />
    </Box>
  );
};
